import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Layout, Layouts } from 'react-grid-layout';

import { type SaveStatus, useDashboardPersistence } from './persistence/useDashboardPersistence';
import { usePersonaPreset } from './presets/usePersonaPreset';
import type { DashboardLayoutState, WidgetConfig, WidgetId } from './types';
import { WIDGET_REGISTRY } from './widgetRegistry';
import {
  type WidgetSizePresetId,
  applyWidgetSizePreset,
  DASHBOARD_COLUMNS,
} from './widgetSizePresets';

let instanceCounter = 0;

/** Unique-per-layout instance id so the same widget can be placed more than once. */
function makeInstanceId(widgetId: WidgetId): string {
  instanceCounter += 1;
  return `${widgetId}__${Date.now().toString(36)}_${instanceCounter}`;
}

/** RGL mutates layout items in place, so cloning avoids stale-reference no-op renders. */
function cloneLayoutItems(items: Layout[] = []): Layout[] {
  return items.map((item) => ({ ...item }));
}

function cloneLayouts(layouts: Layouts): Layouts {
  const next: Layouts = {};
  for (const [bp, items] of Object.entries(layouts)) {
    next[bp] = cloneLayoutItems(items);
  }
  return next;
}

/** Finds the first free top-left slot for a w×h tile, scanning left-to-right, top-to-bottom. */
function findFreeSlot(items: Layout[], w: number, h: number, cols = 12): { x: number; y: number } {
  const overlaps = (x: number, y: number) =>
    items.some(
      (item) => x < item.x + item.w && x + w > item.x && y < item.y + item.h && y + h > item.y
    );

  for (let y = 0; y < 1000; y += 1) {
    for (let x = 0; x <= cols - w; x += 1) {
      if (!overlaps(x, y)) {
        return { x, y };
      }
    }
  }
  // Fallback: drop below every existing tile if the scan above is exhausted.
  const bottom = items.reduce((max, item) => Math.max(max, item.y + item.h), 0);
  return { x: 0, y: bottom };
}

export interface UseDashboardLayoutResult {
  layout: DashboardLayoutState;
  isLoaded: boolean;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  addWidget: (widgetId: WidgetId) => void;
  removeWidget: (instanceId: string) => void;
  onLayoutChange: (current: Layout[], allLayouts: Layouts) => void;
  /** RGL drag/resize start — pauses per-tick layout commits for the gesture. */
  onInteractionStart: () => void;
  /** RGL drag/resize stop — commits the final geometry once and persists it. */
  onInteractionStop: (currentLayout: Layout[]) => void;
  setWidgetSize: (instanceId: string, sizeId: WidgetSizePresetId) => void;
  updateWidgetConfig: (instanceId: string, config: WidgetConfig) => void;
  saveStatus: SaveStatus;
}

/**
 * Owns the dashboard's mutable layout + edit-mode flag, and drives persistence
 * (debounced writes to TableView.customFields).
 */
export function useDashboardLayout(): UseDashboardLayoutResult {
  const { layout: seed } = usePersonaPreset();
  const { loadedLayout, isLoaded, persist, saveStatus, customerId } = useDashboardPersistence(seed);

  const [layoutOverride, setLayoutOverride] = useState<DashboardLayoutState | null>(null);
  const layout = layoutOverride ?? (isLoaded ? loadedLayout : seed);

  // The override describes one customer's dashboard, so it can't outlive a customer change.
  useEffect(() => {
    setLayoutOverride(null);
  }, [customerId]);

  const [isEditing, setIsEditingState] = useState(false);
  const isEditingRef = useRef(false);
  const interactingRef = useRef(false);

  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  const updateLayout = useCallback(
    (updater: (prev: DashboardLayoutState) => DashboardLayoutState) => {
      setLayoutOverride((prev) => updater(prev ?? layoutRef.current));
    },
    []
  );

  const setIsEditing = useCallback((editing: boolean) => {
    isEditingRef.current = editing;
    setIsEditingState(editing);
  }, []);

  const addWidget = useCallback(
    (widgetId: WidgetId) => {
      const definition = WIDGET_REGISTRY[widgetId];
      if (!definition) {
        return;
      }
      const instanceId = makeInstanceId(widgetId);
      const { w, h, minW, minH } = definition.defaultLayout;

      updateLayout((prev) => {
        // Widgets are unique on the dashboard — adding one already present is a no-op
        if (prev.instances.some((inst) => inst.widgetId === widgetId)) {
          return prev;
        }
        const breakpoints = Object.keys(prev.layouts).length ? Object.keys(prev.layouts) : ['lg'];
        // Drop the new tile in the first free slot
        const referenceItems = prev.layouts.lg ?? prev.layouts[breakpoints[0]] ?? [];
        const { x, y } = findFreeSlot(referenceItems, w, h, 12);
        const newItem: Layout = { i: instanceId, x, y, w, h, minW, minH };
        const layouts: Layouts = {};
        for (const bp of breakpoints) {
          layouts[bp] = [...cloneLayoutItems(prev.layouts[bp] ?? []), { ...newItem }];
        }
        const next: DashboardLayoutState = {
          ...prev,
          instances: [...prev.instances, { instanceId, widgetId }],
          layouts,
        };
        persist(next);
        return next;
      });
    },
    [persist, updateLayout]
  );

  const removeWidget = useCallback(
    (instanceId: string) => {
      updateLayout((prev) => {
        const layouts: Layouts = {};
        for (const [bp, items] of Object.entries(prev.layouts)) {
          layouts[bp] = cloneLayoutItems(items.filter((item) => item.i !== instanceId));
        }
        const next: DashboardLayoutState = {
          ...prev,
          instances: prev.instances.filter((inst) => inst.instanceId !== instanceId),
          layouts,
        };
        persist(next);
        return next;
      });
    },
    [persist, updateLayout]
  );

  const onLayoutChange = useCallback(
    (_current: Layout[], allLayouts: Layouts) => {
      // Skip per-tick commits during an active drag/resize
      if (interactingRef.current) {
        return;
      }
      updateLayout((prev) => {
        const next: DashboardLayoutState = {
          ...prev,
          layouts: cloneLayouts({ ...prev.layouts, ...allLayouts }),
        };
        // Only persist drag/resize the user actually made (in edit mode)
        if (isEditingRef.current) {
          persist(next);
        }
        return next;
      });
    },
    [persist, updateLayout]
  );

  const onInteractionStart = useCallback(() => {
    interactingRef.current = true;
  }, []);

  const onInteractionStop = useCallback(
    (currentLayout: Layout[]) => {
      interactingRef.current = false;
      // Commit the gesture's final geometry once
      updateLayout((prev) => {
        const next: DashboardLayoutState = {
          ...prev,
          layouts: { ...prev.layouts, lg: cloneLayoutItems(currentLayout) },
        };
        if (isEditingRef.current) {
          persist(next);
        }
        return next;
      });
    },
    [persist, updateLayout]
  );

  const setWidgetSize = useCallback(
    (instanceId: string, sizeId: WidgetSizePresetId) => {
      updateLayout((prev) => {
        const layouts: Layouts = {};
        for (const [bp, items] of Object.entries(prev.layouts)) {
          const others = items.filter((item) => item.i !== instanceId).map((item) => ({ ...item }));
          const target = items.find((item) => item.i === instanceId);
          if (!target) {
            layouts[bp] = items.map((item) => ({ ...item }));
            continue;
          }
          let resized = applyWidgetSizePreset(target, sizeId);
          // If the larger size would overlap a neighbour, drop it into the first free slot.
          const overlaps = others.some(
            (other) =>
              resized.x < other.x + other.w &&
              resized.x + resized.w > other.x &&
              resized.y < other.y + other.h &&
              resized.y + resized.h > other.y
          );
          if (overlaps) {
            const slot = findFreeSlot(others, resized.w, resized.h, DASHBOARD_COLUMNS);
            resized = { ...resized, x: slot.x, y: slot.y };
          }
          layouts[bp] = [...others, resized];
        }
        const next: DashboardLayoutState = { ...prev, layouts };
        persist(next);
        return next;
      });
    },
    [persist, updateLayout]
  );

  const updateWidgetConfig = useCallback(
    (instanceId: string, config: WidgetConfig) => {
      updateLayout((prev) => {
        const next: DashboardLayoutState = {
          ...prev,
          instances: prev.instances.map((inst) =>
            inst.instanceId === instanceId
              ? { ...inst, config: { ...inst.config, ...config } }
              : inst
          ),
        };
        persist(next);
        return next;
      });
    },
    [persist, updateLayout]
  );

  return useMemo(
    () => ({
      layout,
      isLoaded,
      isEditing,
      setIsEditing,
      addWidget,
      removeWidget,
      onLayoutChange,
      onInteractionStart,
      onInteractionStop,
      setWidgetSize,
      updateWidgetConfig,
      saveStatus,
    }),
    [
      layout,
      isLoaded,
      isEditing,
      setIsEditing,
      addWidget,
      removeWidget,
      onLayoutChange,
      onInteractionStart,
      onInteractionStop,
      setWidgetSize,
      updateWidgetConfig,
      saveStatus,
    ]
  );
}
